import { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '@client/components/layout';
import { UsernameForm } from '@client/components/features/auth';
import { LoadingScreen } from '@client/components/common';
import { TaskPage, FeedPage, AddTaskPage, AdminPage } from '@client/pages';
import { useWebSocket } from '@client/hooks';
import {
  createUser,
  getUser,
  getUserId,
  clearUserId,
  subscribeToSiteDisabled,
} from '@client/lib/api';
import { Camera, Grid3X3, LogOut, Plus } from 'lucide-react';
import type { PhotoWithUser, WebSocketMessage, PhotoLikedEvent } from '@shared/types';

export default function App() {
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('activeTab') || 'task';
  });
  const [userId, setUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [siteDisabledMessage, setSiteDisabledMessage] = useState<string | null>(null);
  
  // Callback refs for real-time updates
  const newPhotoCallbackRef = useRef<((photo: PhotoWithUser) => void) | null>(null);
  const photoLikedCallbackRef = useRef<((event: PhotoLikedEvent['payload']) => void) | null>(null);

  // Persist active tab
  useEffect(() => {
    sessionStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    return subscribeToSiteDisabled(setSiteDisabledMessage);
  }, []);

  // Check for existing user session
  useEffect(() => {
    const checkUser = async () => {
      const storedUserId = getUserId();
      
      if (storedUserId) {
        try {
          const user = await getUser(storedUserId);
          setUserId(user.id);
          setUsername(user.username);
        } catch (error) {
          // User not found or error, clear stored ID
          clearUserId();
        }
      }
      
      setLoading(false);
    };

    checkUser();
  }, []);

  // WebSocket for real-time updates
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'NEW_PHOTO':
        // Create a PhotoWithUser from the payload
        const newPhoto: PhotoWithUser = {
          id: message.payload.id,
          userId: message.payload.userId,
          username: message.payload.username,
          taskDescription: message.payload.taskDescription,
          thumbnailUrl: message.payload.thumbnailUrl,
          originalUrl: message.payload.originalUrl || message.payload.thumbnailUrl,
          likeCount: message.payload.likeCount,
          createdAt: message.payload.createdAt,
          isLikedByCurrentUser: false,
          currentUserReactions: [],
          reactions: [],
          taskId: '',
          originalKey: '',
          thumbnailKey: '',
        };
        newPhotoCallbackRef.current?.(newPhoto);
        break;
      case 'PHOTO_LIKED':
        photoLikedCallbackRef.current?.(message.payload);
        break;
      case 'USER_JOINED':
      case 'USER_LEFT':
        setOnlineUsers(message.payload.activeUsers);
        break;
    }
  }, []);

  useWebSocket(userId, username, {
    onMessage: handleWebSocketMessage,
  });

  const handleUsernameSubmit = async (name: string) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const result = await createUser(name);
      setUserId(result.id);
      setUsername(result.username);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Failed to register');
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePhotoUploaded = useCallback(() => {
    // Switch to feed tab to show the new photo
    setActiveTab('feed');
  }, []);

  const handleNewPhoto = useCallback((callback: (photo: PhotoWithUser) => void) => {
    newPhotoCallbackRef.current = callback;
  }, []);

  const handlePhotoLiked = useCallback((callback: (event: PhotoLikedEvent['payload']) => void) => {
    photoLikedCallbackRef.current = callback;
  }, []);

  const handleLogout = useCallback(() => {
    clearUserId();
    setUserId(null);
    setUsername(null);
    setOnlineUsers(0);
    setActiveTab('task');
    newPhotoCallbackRef.current = null;
    photoLikedCallbackRef.current = null;
    sessionStorage.removeItem('activeTab');
  }, []);

  const tabs = [
    {
      id: 'task',
      label: 'Mission',
      icon: <Camera className="w-6 h-6" />,
      activeIcon: <Camera className="w-6 h-6" strokeWidth={2.5} />,
    },
    {
      id: 'feed',
      label: 'Galerie',
      icon: <Grid3X3 className="w-6 h-6" />,
      activeIcon: <Grid3X3 className="w-6 h-6" strokeWidth={2.5} />,
    },
    {
      id: 'add',
      label: 'Idee',
      icon: <Plus className="w-6 h-6" />,
      activeIcon: <Plus className="w-6 h-6" strokeWidth={2.5} />,
    },
  ];

  // Check if we're on the admin route
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  // Loading state
  if (loading && !isAdminRoute) {
    return <LoadingScreen />;
  }

  // Admin route
  if (isAdminRoute) {
    return <AdminPage />;
  }

  if (siteDisabledMessage) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <div className="text-6xl mb-6">📸</div>
          <h1 className="text-[28px] leading-[34px] font-bold mb-3">Sabrina & Stefan</h1>
          <p className="text-[15px] leading-[20px] text-white/60 mb-3">Wedding Photobooth</p>
          <p className="text-[17px] leading-[24px] text-white/70">
            {siteDisabledMessage}
          </p>
        </div>
      </div>
    );
  }

  // Auth state
  if (!userId) {
    return (
      <UsernameForm
        onSubmit={handleUsernameSubmit}
        loading={authLoading}
        error={authError}
      />
    );
  }

  // Main app
  return (
    <div className="min-h-screen bg-[var(--color-ios-grouped-background)] pb-[calc(50px_+_env(safe-area-inset-bottom))]">
      {activeTab === 'add' && (
        <button
          type="button"
          onClick={handleLogout}
          className="fixed top-[calc(env(safe-area-inset-top)_+_12px)] right-4 z-50 w-11 h-11 rounded-full bg-background/90 backdrop-blur-xl border border-[var(--color-ios-separator)] flex items-center justify-center text-muted-foreground shadow-sm ios-pressable"
          aria-label="Abmelden"
        >
          <LogOut className="w-5 h-5" />
        </button>
      )}

      {activeTab === 'task' && (
        <TaskPage 
          onPhotoUploaded={handlePhotoUploaded} 
          onCameraStateChange={setIsCameraOpen}
        />
      )}
      {activeTab === 'feed' && (
        <FeedPage 
          onNewPhoto={handleNewPhoto} 
          onPhotoLiked={handlePhotoLiked}
          currentUserId={userId}
          onlineUsers={onlineUsers}
        />
      )}
      {activeTab === 'add' && <AddTaskPage />}

      {!isCameraOpen && (
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}
    </div>
  );
}
